import { INTERVIEW_TYPES, TIMEZONES } from '../constants/interview';
import { useEffect, useMemo, useState } from 'react';

function buildVenueAddress(addr) {
  const parts = [];
  if (addr?.label) parts.push(addr.label);
  if (addr?.address) parts.push(addr.address);
  return parts.filter(Boolean).join(' — ').trim();
}

function buildPanelLine(addr) {
  const name = addr?.contactPersonName?.trim() || '';
  const phone = addr?.phone?.trim() || '';
  const email = addr?.email?.trim() || '';
  if (!name && !phone && !email) return '';
  return [name, phone, email].join(' | ');
}

export default function InterviewScheduleForm({ value, onChange, clientContacts = [], clientAddresses = [] }) {
  const set = (field, v) => onChange({ ...value, [field]: v });

  const primaryContact = clientContacts?.[0] || {};
  const addressOptions = useMemo(() => {
    return (clientAddresses || []).map((a, idx) => ({
      id: String(idx),
      label: a?.label || `Location ${idx + 1}`
    }));
  }, [clientAddresses]);

  const [selectedAddrId, setSelectedAddrId] = useState('0');
  const selectedAddr = (clientAddresses || [])[Number(selectedAddrId)] || null;

  useEffect(() => {
    if (value.interviewType !== 'in_person' || !selectedAddr) return;
    const venue = buildVenueAddress(selectedAddr);
    const panel = buildPanelLine(selectedAddr);
    onChange({
      ...value,
      location: venue,
      panelists: panel,
      clientAddressIndex: Number(selectedAddrId),
      contactPersonName: selectedAddr.contactPersonName?.trim() || '',
      contactPhone: selectedAddr.phone?.trim() || '',
      contactEmail: selectedAddr.email?.trim() || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.interviewType, selectedAddrId, selectedAddr]);

  useEffect(() => {
    if (value.interviewType === 'phone') {
      const phone = primaryContact?.phone || '';
      if (!value.meetingLink?.trim() && phone) {
        set('meetingLink', phone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.interviewType, primaryContact?.phone]);

  return (
    <div className="space-y-3 border border-yellow-200 bg-yellow-50/40 p-3">
      <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Schedule interview</p>
      <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
        Candidate will receive email and portal notification with these details. They can reschedule or
        cancel from their dashboard.
      </p>

      <div className="field-with-label">
        <select
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          value={value.interviewType}
          onChange={e => set('interviewType', e.target.value)}
        >
          {INTERVIEW_TYPES.map(t => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
        <label>Interview type *</label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="field-with-label col-span-2">
          <input
            type="datetime-local"
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={value.scheduledAtLocal || value.scheduledAt}
            onChange={e => set('scheduledAtLocal', e.target.value)}
          />
          <label>Date & time *</label>
        </div>
        <div className="field-with-label">
          <input
            type="number"
            min={15}
            max={480}
            step={15}
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={value.durationMinutes}
            onChange={e => set('durationMinutes', Number(e.target.value) || 60)}
          />
          <label>Duration (min)</label>
        </div>
        <div className="field-with-label">
          <select
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={value.timezone}
            onChange={e => set('timezone', e.target.value)}
          >
            {TIMEZONES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <label>Timezone</label>
        </div>
      </div>

      {value.interviewType === 'virtual' && (
        <div className="field-with-label">
          <input
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={value.meetingLink}
            onChange={e => set('meetingLink', e.target.value)}
            placeholder=" "
          />
          <label>Meeting link *</label>
        </div>
      )}

      {value.interviewType === 'in_person' && (
        <div className="space-y-2">
          {addressOptions.length > 0 && (
            <div className="field-with-label">
              <select
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={selectedAddrId}
                onChange={e => setSelectedAddrId(e.target.value)}
              >
                {addressOptions.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label>Choose client interview location</label>
            </div>
          )}
          <div className="field-with-label">
            <input
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              value={value.location}
              onChange={e => set('location', e.target.value)}
              placeholder=" "
            />
            <label>Venue / address *</label>
          </div>
          {!addressOptions.length && (
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              No client locations saved yet. Add them in Clients → Edit → Interview locations.
            </p>
          )}
        </div>
      )}

      {value.interviewType === 'phone' && (
        <div className="field-with-label">
          <input
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={value.meetingLink}
            onChange={e => set('meetingLink', e.target.value)}
            placeholder=" "
          />
          <label>Phone / dial-in details</label>
        </div>
      )}

      <div className="field-with-label">
        <input
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          value={value.panelists}
          onChange={e => set('panelists', e.target.value)}
          placeholder=" "
        />
        <label>Interviewer(s) / panel</label>
        <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
          For in-person interviews, auto-filled as Name | Mobile | Email from the selected location.
        </p>
      </div>

      <div className="field-with-label">
        <input
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          value={value.dressCode}
          onChange={e => set('dressCode', e.target.value)}
          placeholder=" "
        />
        <label>Dress code</label>
      </div>

      <div className="field-with-label">
        <textarea
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          rows={3}
          value={value.instructions}
          onChange={e => set('instructions', e.target.value)}
          placeholder=" "
        />
        <label>Instructions & requirements for candidate *</label>
      </div>
    </div>
  );
}
